import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { API } from 'utilities/api';
import { isPlatformHost } from 'config';

/**
 * The dashboard, running on a brand's own domain.
 *
 * On techproone.com the dashboard is the platform's: it lists the brands you
 * have access to, lets you switch between them and make another. On
 * bakery.com/dashboard it must be the bakery's — one brand, no switcher, no
 * way to create another, and no mention of us anywhere on it.
 *
 * The host decides, and that is the whole mechanism. The brand that owns this
 * domain is the brand this dashboard is for; nothing the person clicks can
 * change it, because there is nothing to click.
 *
 * ## What this is and is not
 *
 * It is presentation and scope. It is NOT the security boundary, and reading
 * it as one would be the mistake: every endpoint already checks that the
 * caller has access to the brand named in the request, and that check is what
 * actually stops someone reaching another brand's data. What this adds is
 * that on this domain there is no other brand to reach for — which is a
 * promise to the brand's own staff, not a defence against them.
 *
 * `ready` matters. Until the host has been resolved we do not know whether to
 * draw a brand switcher, and drawing one that then vanishes is worse than
 * waiting a moment: it tells the brand's staff that other brands exist.
 */

const WhiteLabelContext = createContext({
  active: false, ready: true, brand: null, brandId: null,
});

export function WhiteLabelProvider({ children }) {
  // A platform host is never white-labelled, and knowing that needs no
  // request — so those sessions are ready immediately.
  const onPlatform = isPlatformHost();
  const [state, setState] = useState({
    active: false, ready: onPlatform, brand: null, brandId: null,
  });

  useEffect(() => {
    if (onPlatform) return undefined;
    let cancelled = false;

    API.publicResolvedRoute()
      .then((res) => {
        if (cancelled) return;
        const brand = res?.brand || null;
        setState({
          active: Boolean(brand?._id),
          ready: true,
          brand,
          brandId: brand?._id || null,
        });
      })
      .catch(() => {
        // A domain we cannot resolve is not a brand's domain. Fall back to
        // the ordinary dashboard rather than locking someone out of their
        // own account because a lookup failed.
        if (!cancelled) setState({ active: false, ready: true, brand: null, brandId: null });
      });

    return () => { cancelled = true; };
  }, [onPlatform]);

  const value = useMemo(() => state, [state]);
  return <WhiteLabelContext.Provider value={value}>{children}</WhiteLabelContext.Provider>;
}

export const useWhiteLabel = () => useContext(WhiteLabelContext);

export default WhiteLabelContext;
